import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import messagingApi from '../../services/messagingApi';

const TestMessageModal = ({ show, onHide, template, onMessageSent }) => {
  // Default to first enabled channel
  const defaultChannel = template?.channels?.whatsapp?.enabled ? 'whatsapp' : 'sms';

  const [recipientPhone, setRecipientPhone] = useState('');
  const [channel, setChannel] = useState(defaultChannel);
  const [variables, setVariables] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Extract variable names from template content
  const getTemplateVariables = () => {
    const content = template?.content?.ar?.body || template?.content?.ar;
    if (!content) return [];
    const matches = content.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    return matches.map(m => m.replace(/[{}]/g, ''));
  };

  const templateVariables = getTemplateVariables();

  const handleVariableChange = (varName, value) => {
    setVariables(prev => ({ ...prev, [varName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!recipientPhone || recipientPhone.length < 10) {
      setError('الرجاء إدخال رقم هاتف صحيح');
      return;
    }

    // Check if all variables are filled
    const missingVars = templateVariables.filter(v => !variables[v] || variables[v].trim() === '');
    if (missingVars.length > 0) {
      setError(`الرجاء إدخال قيم لجميع المتغيرات: ${missingVars.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      await messagingApi.sendTestMessage(template._id, {
        recipientPhone,
        channel,
        variables,
        language: 'ar'
      });

      setSuccess(true);
      if (onMessageSent) {
        onMessageSent();
      }

      // Reset form after 2 seconds
      setTimeout(() => {
        setRecipientPhone('');
        setVariables({});
        setSuccess(false);
        onHide();
      }, 2000);
    } catch (err) {
      console.error('Error sending test message:', err);
      setError(err.response?.data?.error || 'فشل في إرسال الرسالة التجريبية');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRecipientPhone('');
    setVariables({});
    setError(null);
    setSuccess(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} dir="rtl" centered>
      <Modal.Header closeButton>
        <Modal.Title>إرسال رسالة تجريبية</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success">
              تم إرسال الرسالة التجريبية بنجاح!
            </Alert>
          )}

          {/* Template Info */}
          <div className="mb-3">
            <h6>{template.name?.ar}</h6>
            <p className="text-muted small mb-0">{template.description?.ar}</p>
          </div>

          {/* Recipient Phone */}
          <Form.Group className="mb-3">
            <Form.Label>رقم الهاتف</Form.Label>
            <Form.Control
              type="tel"
              placeholder="966XXXXXXXXX"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              required
              dir="ltr"
            />
            <Form.Text className="text-muted">
              مثال: 966501234567
            </Form.Text>
          </Form.Group>

          {/* Channel Selection */}
          <Form.Group className="mb-3">
            <Form.Label>القناة</Form.Label>
            <Form.Select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              dir="rtl"
            >
              {template.channels?.whatsapp?.enabled && (
                <option value="whatsapp">📱 واتساب</option>
              )}
              {template.channels?.sms?.enabled && (
                <option value="sms">💬 SMS</option>
              )}
            </Form.Select>
          </Form.Group>

          {/* Template Variables */}
          {templateVariables.length > 0 && (
            <>
              <h6 className="mb-3">متغيرات القالب</h6>
              {templateVariables.map((varName) => (
                <Form.Group key={varName} className="mb-3">
                  <Form.Label>{varName}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={`أدخل قيمة ${varName}`}
                    value={variables[varName] || ''}
                    onChange={(e) => handleVariableChange(varName, e.target.value)}
                    required
                    dir="rtl"
                  />
                </Form.Group>
              ))}
            </>
          )}

          {/* Preview */}
          {(template.content?.ar?.body || template.content?.ar) && (
            <div className="mb-3">
              <h6>معاينة الرسالة</h6>
              <div
                className="p-3 bg-light rounded"
                style={{ whiteSpace: 'pre-wrap', direction: 'rtl' }}
              >
                {(template.content.ar.body || template.content.ar).replace(/\{\{(\w+)\}\}/g, (match, varName) => {
                  return variables[varName] || `[${varName}]`;
                })}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            إلغاء
          </Button>
          <Button variant="primary" type="submit" disabled={loading || success}>
            {loading ? 'جاري الإرسال...' : 'إرسال'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TestMessageModal;
